import { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "../lib/api";

const INSTAGRAM_URL = "https://instagram.com/egemodels";

const MENU_PRIMARY = [
  { to: "/models", label: "Models", testid: "menu-models" },
  { to: "/archive", label: "Latest work", testid: "menu-archive" },
  { to: "/about", label: "About", testid: "menu-about" },
  { to: "/services", label: "Services", testid: "menu-services" },
  { to: "/apply", label: "Get scouted", testid: "menu-get-scouted" },
  { to: "/casting", label: "Now casting", testid: "menu-casting" },
  { to: "/contact", label: "Contact", testid: "menu-contact" },
];
const MENU_SECONDARY = [
  { to: "/status", label: "Check application status" },
  { to: "/apply/guide", label: "Photo guide" },
  { to: "/faq", label: "FAQ" },
  { to: "/code-of-conduct", label: "Code of conduct" },
];

/* Ford-style header: hamburger · large wordmark · "SEARCH ____" at the right.
   All navigation lives in the full-screen menu. */
export default function Nav() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [inlineFocus, setInlineFocus] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [models, setModels] = useState(null);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const navigate = useNavigate();
  // Light (white) header only over the dark model-profile hero; home hero is light.
  const overDarkHero = /^\/models\/.+/.test(location.pathname);
  const lightText = overDarkHero && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if ((!searchOpen && !query.trim()) || models !== null) return;
    api.get("/models").then((r) => setModels(r.data || [])).catch(() => setModels([]));
  }, [searchOpen, query, models]);

  const overlayOpen = searchOpen || menuOpen;
  useEffect(() => {
    if (!overlayOpen) return;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") { setSearchOpen(false); setMenuOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [overlayOpen]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !models) return [];
    return models.filter((m) => (m.name || "").toLowerCase().includes(q)).slice(0, 8);
  }, [query, models]);

  const closeSearch = () => { setSearchOpen(false); setQuery(""); };
  const closeMenu = () => setMenuOpen(false);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    closeSearch();
    navigate(q ? `/models?q=${encodeURIComponent(q)}` : "/models");
  };

  return (
    <>
      <header
        data-testid="site-nav"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-[#FFFFFF]/90 backdrop-blur-2xl border-b border-black/10"
            : "bg-transparent"
        }`}
      >
        <div
          className={`max-w-[1700px] mx-auto px-4 sm:px-6 lg:px-8 h-16 md:h-24 flex items-center justify-between transition-colors duration-500 ${
            lightText ? "text-white" : "text-black"
          }`}
        >
          {/* Left — hamburger + wordmark */}
          <div className="flex items-center gap-5 md:gap-12 min-w-0">
            <button
              type="button"
              data-testid="nav-menu-toggle"
              aria-label="Open menu"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(true)}
              className="shrink-0 group flex flex-col justify-center gap-[5px] w-7 h-7 transition-opacity hover:opacity-60"
            >
              <span className="block h-[1.5px] w-6 bg-current"/>
              <span className="block h-[1.5px] w-6 bg-current"/>
              <span className="block h-[1.5px] w-6 bg-current"/>
            </button>
            <Link to="/" data-testid="nav-logo" className="shrink-0 transition-opacity hover:opacity-70">
              <img
                src={lightText ? "/brand/ege-logo-white.png" : "/brand/ege-logo-black.png"}
                alt="Ege Models"
                className="h-[26px] md:h-10 w-auto"
              />
            </Link>
          </div>

          {/* Right — "SEARCH ____" (desktop) / magnifier (mobile) */}
          <div className="flex items-center">
            <div className="hidden md:block relative">
              <form
                onSubmit={submitSearch}
                data-testid="nav-search-form"
                className="flex items-end gap-3"
              >
                <label
                  htmlFor="nav-search-input"
                  className="text-[13px] font-semibold tracking-[0.04em] uppercase leading-none pb-[3px] cursor-text"
                >
                  Search
                </label>
                <input
                  id="nav-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onFocus={() => setInlineFocus(true)}
                  onBlur={() => setTimeout(() => setInlineFocus(false), 150)}
                  onKeyDown={(e) => { if (e.key === "Escape") { setQuery(""); e.target.blur(); } }}
                  aria-label="Search the board"
                  data-testid="nav-search-input"
                  className={`w-24 lg:w-28 focus:w-44 bg-transparent outline-none border-b text-[13px] tracking-[0.02em] pb-[2px] transition-[width,border-color] duration-300 ${
                    lightText ? "border-white focus:border-white" : "border-black focus:border-black"
                  }`}
                />
                <button type="submit" aria-label="Search" className="sr-only">Search</button>
              </form>

              {/* Live suggestions dropdown */}
              {inlineFocus && query.trim() && models && (
                <div
                  data-testid="nav-search-dropdown"
                  onMouseDown={(e) => e.preventDefault()}
                  className="absolute top-full right-0 mt-3 w-64 bg-white text-black border border-black/10 shadow-[0_16px_40px_-12px_rgba(0,0,0,0.25)] z-50"
                >
                  {matches.length > 0 ? (
                    <ul className="py-1">
                      {matches.slice(0, 6).map((m) => (
                        <li key={m.id}>
                          <Link
                            to={`/models/${m.slug}`}
                            onClick={() => { setQuery(""); setInlineFocus(false); }}
                            data-testid={`nav-search-suggestion-${m.slug}`}
                            className="flex items-center gap-3 px-3 py-2.5 hover:bg-black/5 transition-colors"
                          >
                            {m.cover_image && (
                              <img src={m.cover_image} alt="" className="w-8 h-10 object-cover object-top shrink-0 bg-[#F2F2F1]"/>
                            )}
                            <span className="text-[13px] tracking-[0.01em] truncate">{m.name}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p data-testid="nav-search-no-matches" className="px-3 py-3 text-[11px] uppercase tracking-[0.08em] text-zinc-500">
                      No matches — Enter to search the board
                    </p>
                  )}
                </div>
              )}
            </div>
            <button
              type="button"
              data-testid="nav-search-toggle"
              aria-label="Search the board"
              onClick={() => setSearchOpen(true)}
              className="md:hidden flex items-center gap-2 transition-opacity hover:opacity-60"
            >
              <span className="text-[12px] font-semibold tracking-[0.04em] uppercase">Search</span>
              <Search size={16} strokeWidth={2}/>
            </button>
          </div>
        </div>
      </header>

      {menuOpen && createPortal(
        <div
          data-testid="site-menu"
          role="dialog"
          aria-modal="true"
          aria-label="Site menu"
          className="fixed inset-0 z-[100] bg-[#FFFFFF] text-black flex flex-col menu-overlay-in overflow-y-auto"
        >
          <div className="relative max-w-[1700px] w-full mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-24 shrink-0">
            <Link to="/" onClick={closeMenu} className="shrink-0">
              <img src="/brand/ege-logo-black.png" alt="Ege Models" className="h-[26px] md:h-10 w-auto"/>
            </Link>
            <button
              type="button"
              data-testid="menu-close"
              aria-label="Close menu"
              onClick={closeMenu}
              className="group flex items-center gap-3 transition-opacity hover:opacity-60"
            >
              <span className="hidden sm:block text-[13px] font-semibold tracking-[0.04em] uppercase">Close</span>
              <span className="relative block w-6 h-6">
                <span className="absolute top-1/2 left-0 w-6 h-[1.5px] bg-current rotate-45"/>
                <span className="absolute top-1/2 left-0 w-6 h-[1.5px] bg-current -rotate-45"/>
              </span>
            </button>
          </div>

          <div className="max-w-[1700px] w-full mx-auto px-4 sm:px-6 lg:px-8 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-10 pb-16 pt-6 md:pt-10">
            <nav aria-label="Primary" className="lg:col-span-7">
              <ul className="space-y-1 md:space-y-2">
                {MENU_PRIMARY.map((l, i) => (
                  <li key={l.to} className="menu-item" style={{ animationDelay: `${0.05 + i * 0.045}s` }}>
                    <Link
                      to={l.to}
                      onClick={closeMenu}
                      data-testid={l.testid}
                      className={`inline-block font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight transition-colors ${
                        location.pathname === l.to ? "text-black" : "text-zinc-800 hover:text-zinc-400"
                      }`}
                    >
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="lg:col-span-5 lg:pt-3 flex flex-col justify-between gap-10">
              <ul className="space-y-3 menu-item" style={{ animationDelay: "0.3s" }}>
                {MENU_SECONDARY.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} onClick={closeMenu} className="text-sm md:text-base text-zinc-600 hover:text-black transition-colors link-underline">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <div className="menu-item space-y-4" style={{ animationDelay: "0.38s" }}>
                <p className="label-overline text-zinc-400">Follow</p>
                <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                  <a href={INSTAGRAM_URL} target="_blank" rel="noreferrer" data-testid="menu-instagram" className="link-underline">Instagram</a>
                  <a href="https://www.tiktok.com/@egemodels" target="_blank" rel="noreferrer" className="link-underline">TikTok</a>
                </div>
                <p className="label-overline text-zinc-400 pt-4">Office</p>
                <p className="text-sm text-zinc-600 leading-relaxed">
                  Puck van Heelstraat 142<br/>3077 MG Rotterdam, Netherlands<br/>
                  <a href="mailto:info@egemodels.com" className="link-underline text-black">info@egemodels.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {searchOpen && createPortal(
        <div
          data-testid="search-overlay"
          className="fixed inset-0 z-[100] bg-[#FFFFFF] text-black flex flex-col menu-overlay-in overflow-y-auto"
        >
          <div className="relative max-w-[1700px] w-full mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 md:h-24 shrink-0">
            <Link to="/" onClick={closeSearch} className="shrink-0">
              <img src="/brand/ege-logo-black.png" alt="Ege Models" className="h-[26px] md:h-10 w-auto"/>
            </Link>
            <button
              type="button"
              data-testid="search-close"
              aria-label="Close search"
              onClick={closeSearch}
              className="group flex items-center gap-3 transition-opacity hover:opacity-60"
            >
              <span className="hidden sm:block text-[13px] font-semibold tracking-[0.04em] uppercase">Close</span>
              <span className="relative block w-6 h-6">
                <span className="absolute top-1/2 left-0 w-6 h-[1.5px] bg-current rotate-45"/>
                <span className="absolute top-1/2 left-0 w-6 h-[1.5px] bg-current -rotate-45"/>
              </span>
            </button>
          </div>

          <div className="max-w-[1100px] w-full mx-auto px-6 lg:px-12 flex-1 flex flex-col justify-center pb-24">
            <p className="label-overline text-zinc-400 menu-item" style={{ animationDelay: "0.05s" }}>Search the board</p>
            <form onSubmit={submitSearch} className="menu-item" style={{ animationDelay: "0.12s" }}>
              <input
                autoFocus
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type a name…"
                className="w-full bg-transparent border-b border-black/15 focus:border-black font-serif font-light tracking-tighter text-5xl md:text-7xl py-5 outline-none placeholder:text-zinc-200 transition-colors"
              />
            </form>

            {matches.length > 0 && (
              <ul className="mt-10 space-y-3 menu-item" style={{ animationDelay: "0.05s" }} data-testid="search-results">
                {matches.map((m) => (
                  <li key={m.id}>
                    <Link
                      to={`/models/${m.slug}`}
                      onClick={closeSearch}
                      data-testid={`search-result-${m.slug}`}
                      className="group inline-flex items-baseline gap-4 font-serif text-2xl md:text-3xl tracking-tight text-zinc-800 hover:text-zinc-500 transition-colors"
                    >
                      {m.name}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
            {query.trim() && models && matches.length === 0 && (
              <p className="mt-10 text-zinc-500 font-serif text-xl" data-testid="search-no-results">
                No talent by that name — press Enter to search the full board.
              </p>
            )}

            <p className="label-overline text-zinc-400 mt-12 menu-item" style={{ animationDelay: "0.2s" }}>
              Press Enter to search the board ↵
            </p>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
